import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useNavigate } from 'react-router';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import type { Customer, LoginFormData } from '@jwp/ott-common/types/account';
import { PATH_ACTIVATETV, PATH_HOME, PATH_FORGOT_PASSWORD } from '@jwp/ott-common/src/paths';
import useForm from '@jwp/ott-hooks-react/src/useForm';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import Visibility from '@jwp/ott-theme/assets/icons/eye-on.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/eye-off.svg?react';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import { useAuth } from '@jwp/ott-hooks-react/src/useAuth';
import { getUserProfile } from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';

import FormFeedback from '../FormFeedback/FormFeedback';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Link from '../Link/Link';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';

import styles from './SignIn.module.scss';

function BrandLogo({ appConfig, siteName }: Readonly<{ appConfig: any; siteName: string | undefined }>) {
  return (
    <div className={styles.brand}>
      {appConfig?.branding?.logo_url ? (
        <img src={appConfig.branding.logo_url} alt={siteName} className={styles.brandLogoImage} />
      ) : (
        <div className={styles.brandLogo} />
      )}
    </div>
  );
}

function SignInBackgroundVideo() {
  return (
    <div className={styles.videoLayer} aria-hidden>
      <video className={styles.videoBackground} autoPlay muted loop playsInline preload="metadata">
        <source src="/videos/signin-background.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

const SignIn: React.FC = () => {
  const { t } = useTranslation('account');
  const navigate = useNavigate();
  const { siteName, appConfig } = useConfigStore((s) => ({ siteName: s.config.siteName, appConfig: s.appConfig }));
  const recaptchaAPI = useConfigStore((s) => s.appConfig.lambda.recaptcha_verification);
  const user = useAccountStore((s) => s.user);
  const accountLoading = useAccountStore((s) => s.loading);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { authentication_type, isGoogleCaptchaEnabled } = useAppConfig();
  const deviceType = useGADeviceType();
  const announce = useAriaAnnouncer();
  const qr = useQueryParam('qr');
  const [viewPassword, toggleViewPassword] = useToggle();
  const { meetSignIn } = useAuth();

  const { values, errors, submitting, validationSchemaError, handleSubmit, handleChange } = useForm<LoginFormData>({
    initialValues: { email: '', password: '' },
    silentValidation: true,
    validationSchema: object().shape({
      email: string().required(t('login.field_required', { field: t('login.username', { defaultValue: 'Username / Email ID' }) })),
      password: string().required(t('login.field_required', { field: t('login.password') })),
    }),
    onSubmit: async ({ email: username, password }) => {
      if (isGoogleCaptchaEnabled) {
        if (!executeRecaptcha) {
          console.warn('reCAPTCHA not ready');
          throw new Error('reCAPTCHA not ready');
        }

        const token = await executeRecaptcha('login');
        const verifyResponse = await fetch(recaptchaAPI, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const { success, score, action } = await verifyResponse.json();

        if (!success || score < 0.5 || action !== 'login') {
          throw new Error('reCAPTCHA validation failed');
        }
      }

      const result = await meetSignIn({ username, password });

      if (result.error || (result as any).errors?.length) {
        const error = result.error || (result as any).errors[0];
        throw new Error(error.message || t('login.unexpected_error'));
      }

      const signInOutput = result.data?.meetSignIn;

      if (signInOutput?.__typename === 'multiUserOutput') {
        throw new Error(t('login.wrong_combination'));
      }

      if (signInOutput?.__typename !== 'signInSuccessOutput') {
        throw new Error(t('login.unexpected_error'));
      }

      const accessToken = signInOutput.AccessToken;
      const idToken = signInOutput.IdToken;
      const refreshToken = signInOutput.RefreshToken;
      const userDetails = signInOutput.userDetails;

      if (!accessToken || !idToken || !refreshToken || !userDetails?.uId) {
        throw new Error(t('login.unexpected_error'));
      }

      localStorage.setItem(
        'jwapp.auth',
        JSON.stringify({
          accessToken,
          refreshToken,
        }),
      );
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem(
        'inplayer_token',
        JSON.stringify({
          token: accessToken,
          refreshToken,
        }),
      );
      localStorage.setItem('id_token', idToken);
      localStorage.setItem('refresh_token', refreshToken);

      const authenticatedUser: Customer = {
        id: userDetails.uId,
        firstName: userDetails.uFirstName || username,
        fullName: userDetails.uFirstName || username,
        email: username,
        role: userDetails.uRole,
        metadata: {},
      };

      localStorage.setItem('katon_user', JSON.stringify(authenticatedUser));
      
      const additionalProfileData = await getUserProfile();
      const finalUser = additionalProfileData 
        ? { ...authenticatedUser, ...additionalProfileData } 
        : authenticatedUser;

      useAccountStore.setState({
        user: finalUser,
        loading: false,
      });

    },
    onSubmitSuccess: () => {
      const userId = useAccountStore.getState().user?.id;
      sendGAEvent('login_success', {
        provider: authentication_type,
        method: authentication_type,
        userid_no: userId,
        device_type: deviceType,
      });

      announce(t('login.sign_in_success'), 'success');

      if (!qr) {
        navigate(PATH_HOME);
      }
    },
    onSubmitError: ({ error, resetValue }) => {
      let errorMessage = 'Unknown error';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      sendGAEvent('login_failure', {
        email: values.email,
        error_message: errorMessage,
        device_type: deviceType,
      });

      const lowerCaseError = errorMessage.toLowerCase();
      const guestRedirectErrors = ['toomanyrequestsexception', 'toomanyfailedattemptsexception', 'limitexceededexception'];
      const shouldHandleAsGuest = guestRedirectErrors.some((err) => lowerCaseError.includes(err));

      if (shouldHandleAsGuest) {
        const guestUser: Customer = {
          id: 'guest_user',
          firstName: 'Guest',
          fullName: 'Guest User',
          email: '',
          role: 'guest',
          metadata: {},
        };

        localStorage.setItem('katon_user', JSON.stringify(guestUser));

        useAccountStore.setState({
          user: guestUser,
          loading: false,
        });

        navigate(PATH_HOME);
        return;
      }

      showToast(errorMessage, 'error');
      resetValue('password');
    },
  });

  useEffect(() => {
    let hasValidToken = false;

    const authRaw = localStorage.getItem('jwapp.auth');

    if (authRaw) {
      try {
        const auth = JSON.parse(authRaw);

        if (auth?.accessToken && auth?.refreshToken) {
          hasValidToken = true;
        }
      } catch {
        console.error('Invalid auth JSON');
      }
    }

    if (localStorage.getItem('access_token')) hasValidToken = true;
    if (localStorage.getItem('inplayer_token')) hasValidToken = true;

    if (hasValidToken && !accountLoading) {
      if (qr) {
        navigate(PATH_ACTIVATETV);
        return;
      }
      navigate(PATH_HOME);
    }
  }, [navigate, qr, user, accountLoading]);

  if (user) {
    return <Navigate to={PATH_HOME} replace />;
  }

  return (
    <div className={styles.root}>
      <Helmet>
        <title>{t('login.sign_in', 'Sign In')}</title>
      </Helmet>
      <SignInBackgroundVideo />
      <BrandLogo appConfig={appConfig} siteName={siteName} />
      <main className={styles.main}>
        <div className={styles.overlayWrap}>
          {submitting ? (
            <div className={styles.loaderLayer} aria-live="polite" aria-busy>
              <LoadingOverlay transparentBackground inline />
            </div>
          ) : null}
          <div className={styles.card}>
            <h1 className={styles.cardTitle}>{t('login.sign_in', 'Sign In')}</h1>
            <p className={styles.cardSubtitle}>
              {siteName
                ? t('login.sign_in_subtitle', { siteName, defaultValue: `Sign in to your play account` })
                : t('login.sign_in_subtitle_generic', { defaultValue: 'Sign in to your account' })}
            </p>

            <form onSubmit={handleSubmit} noValidate aria-busy={submitting}>

              <div className={styles.field}>
                <label htmlFor="signin-email" className={styles.fieldLabel}>
                  {t('login.username', { defaultValue: 'Username / Email ID' })}
                  <span className={styles.required} aria-hidden>
                    *
                  </span>
                </label>
                <div className={styles.inputWrap}>
                  <input
                    id="signin-email"
                    name="email"
                    type="text"
                    autoComplete="username"
                    className={styles.input}
                    value={values.email}
                    onChange={handleChange}
                    placeholder={t('login.username_placeholder', { defaultValue: 'Enter username / email id' })}
                    required
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'signin-email-error' : undefined}
                  />
                </div>
                <p id="signin-email-error" className={styles.helperError} role="alert" style={{ visibility: errors.email ? 'visible' : 'hidden' }}>
                  {errors.email || '\u00A0'}
                </p>
              </div>

              <div className={styles.field}>
                <label htmlFor="signin-password" className={styles.fieldLabel}>
                  {t('login.password')}
                  <span className={styles.required} aria-hidden>
                    *
                  </span>
                </label>
                <div className={`${styles.inputWrap} ${styles.inputWithToggle}`}>
                  <input
                    id="signin-password"
                    name="password"
                    type={viewPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={styles.input}
                    value={values.password}
                    onChange={handleChange}
                    placeholder={t('login.password_placeholder')}
                    required
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? 'signin-password-error' : undefined}
                  />
                  <button
                    type="button"
                    className={styles.toggleVisibility}
                    onClick={() => toggleViewPassword()}
                    aria-label={t('login.view_password')}
                    aria-pressed={viewPassword}
                  >
                    <Icon icon={viewPassword ? Visibility : VisibilityOff} />
                  </button>
                </div>
                <p id="signin-password-error" className={styles.helperError} role="alert" style={{ visibility: errors.password ? 'visible' : 'hidden' }}>
                  {errors.password || '\u00A0'}
                </p>
              </div>

              <div className={styles.forgotPasswordContainer}>
                <Link to={PATH_FORGOT_PASSWORD} className={styles.forgotPassword}>
                  {t('reset.forgot_password_username_link', { defaultValue: 'Forgot Username / Password?' })}
                </Link>
              </div>

              <Button type="submit" color="primary" fullWidth size="large" disabled={submitting} label={t('login.sign_in', 'Sign In')} />
            </form>

            {/* <p className={styles.footer}>
              {t('login.not_registered')}{' '}
              {isCognitoEnabled ? (
                <a className={styles.signUpLink} href={AUTH_URL}>
                  {t('login.sign_up')}
                </a>
              ) : (
                <Link className={styles.signUpLink} to={signUpModalUrl}>
                  {t('login.sign_up')}
                </Link>
              )}
            </p> */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignIn;
